import { EmptyState, Loading } from "../../app/Status";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  normalizePairCode,
  SCREEN_DAYS,
  SCREEN_ONLINE_MINUTES,
  SCREEN_PROFILE_HINTS,
  SCREEN_PROFILE_LABELS,
  SCREEN_NIGHT_DEFAULT,
  SCREEN_PROFILES,
  SCREEN_THEME_LABELS,
  SCREEN_THEMES,
  type BuildingDto,
  type ScreenDto,
  type ScreenProfile,
  type ScreenTheme,
} from "@klantpunt/shared";
import { Meta } from "../../app/Meta";
import { Modal } from "../../app/Modal";
import { IconCopy, IconExternal, IconMonitor } from "../../app/icons";
import { api, ApiError } from "../../lib/api";
import { useToast } from "../../lib/toast";

const screensKey = (buildingId: string) => ["building-screens", buildingId] as const;
const errMsg = (e: unknown) => (e instanceof ApiError ? e.message : "Dat lukte niet; probeer het opnieuw");
const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" });
const fmtSeen = (iso: string) => {
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (min < 1) return "zojuist";
  if (min < 60) return `${min} min geleden`;
  if (min < 24 * 60) return `${Math.round(min / 60)} uur geleden`;
  return fmtDate(iso);
};

const hh = (h: number) => `${String(h).padStart(2, "0")}:00`;
const HOURS = Array.from({ length: 24 }, (_, h) => h);

type Days = (typeof SCREEN_DAYS)[number] | null;
const DAY_LABELS: { value: Days; label: string }[] = [
  { value: null, label: "Tot intrekken" },
  { value: 30, label: "30 dagen" },
  { value: 90, label: "90 dagen" },
  { value: 365, label: "1 jaar" },
];

function state(s: ScreenDto): { label: string; cls: string } {
  if (s.revokedAt) return { label: "Ingetrokken", cls: "" };
  if (s.expiresAt && new Date(s.expiresAt) < new Date()) return { label: "Verlopen", cls: "" };
  if (!s.lastSeenAt) return { label: "Nog niet geopend", cls: "" };
  const online = Date.now() - new Date(s.lastSeenAt).getTime() < SCREEN_ONLINE_MINUTES * 60_000;
  return online ? { label: "Online", cls: "tone tone-ok" } : { label: "Offline", cls: "tone tone-warn" };
}

/**
 * Gebouw › Schermen (docs/SCHERMEN.md): alleen-lezen dashboards voor een tv in de lobby of het beheerkantoor.
 * Zonder inloggen, met een link die je altijd kunt intrekken.
 */
export function ScreensPanel({
  building: b,
  creating,
  onCreateDone,
}: {
  building: BuildingDto;
  creating: boolean;
  onCreateDone: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const screens = useQuery({
    queryKey: screensKey(b.id),
    queryFn: () => api<ScreenDto[]>(`/buildings/${b.id}/screens`),
    refetchInterval: 60_000,
  });

  const setProfile = async (s: ScreenDto, profile: ScreenProfile) => {
    try {
      qc.setQueryData(
        screensKey(b.id),
        await api<ScreenDto[]>(`/screens/${s.id}`, { method: "PATCH", body: { profile } }),
      );
      toast(`${s.name} toont nu ${SCREEN_PROFILE_LABELS[profile].toLowerCase()}`);
    } catch (e) {
      toast(errMsg(e));
    }
  };
  const revoke = async (s: ScreenDto) => {
    if (!window.confirm(`${s.name} intrekken? Het scherm toont dan meteen niets meer.`)) return;
    try {
      qc.setQueryData(screensKey(b.id), await api<ScreenDto[]>(`/screens/${s.id}`, { method: "DELETE" }));
      toast("Scherm ingetrokken");
    } catch (e) {
      toast(errMsg(e));
    }
  };

  const [editing, setEditing] = useState<ScreenDto | null>(null);
  const list = screens.data ?? [];
  return (
    <section className="panel" aria-labelledby="bs-screens">
      <h2 id="bs-screens">Schermen</h2>
      <p className="muted small">
        Een alleen-lezen dashboard voor een tv of scherm in het gebouw: wie er vandaag komt, belangrijke storingen,
        meldingen en het weer. Het ververst zichzelf elke minuut en werkt zonder inloggen.
      </p>
      {screens.isPending ? (
        <Loading />
      ) : !list.length ? (
        <EmptyState
          icon={IconMonitor}
          title="Nog geen schermen"
          text="Maak er een met + Scherm en open de link op de tv."
        />
      ) : (
        <ul className="dir-items screens-list" aria-label="Schermen">
          {list.map((s) => {
            const st = state(s);
            const active = !s.revokedAt && !(s.expiresAt && new Date(s.expiresAt) < new Date());
            return (
              <li key={s.id} className={active ? "" : "muted"}>
                <span className="grow">
                  <strong>{s.name}</strong>
                  <span className="muted small block">
                    <Meta
                      items={[
                        SCREEN_PROFILE_LABELS[s.profile],
                        SCREEN_THEME_LABELS[s.settings.theme],
                        s.settings.night
                          ? `nachtstand ${hh(s.settings.night.from)}–${hh(s.settings.night.until)}`
                          : "geen nachtstand",
                        s.lastSeenAt && `laatst gezien ${fmtSeen(s.lastSeenAt)}`,
                        active && (s.expiresAt ? `tot ${fmtDate(s.expiresAt)}` : "tot intrekken"),
                        s.createdBy,
                      ]}
                    />
                  </span>
                </span>
                <span className={`chip ${st.cls}`.trim()}>{st.label}</span>
                {active && (
                  <>
                    <select
                      className="screens-profile"
                      aria-label={`Profiel van ${s.name}`}
                      value={s.profile}
                      onChange={(e) => void setProfile(s, e.target.value as ScreenProfile)}
                    >
                      {SCREEN_PROFILES.map((p) => (
                        <option key={p} value={p}>
                          {SCREEN_PROFILE_LABELS[p]}
                        </option>
                      ))}
                    </select>
                    <button type="button" className="btn small ghost" onClick={() => setEditing(s)}>
                      Instellen
                    </button>
                    <button type="button" className="btn small ghost danger" onClick={() => void revoke(s)}>
                      Intrekken
                    </button>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {editing && (
        <SettingsDialog
          screen={editing}
          onClose={() => setEditing(null)}
          onSaved={(rows) => {
            qc.setQueryData(screensKey(b.id), rows);
            setEditing(null);
            toast(`${editing.name} neemt de instellingen binnen een minuut over`);
          }}
        />
      )}
      {creating && (
        <ScreenDialog
          building={b}
          onClose={onCreateDone}
          onCreated={() => void qc.invalidateQueries({ queryKey: screensKey(b.id) })}
        />
      )}
    </section>
  );
}

function ScreenDialog({
  building: b,
  onClose,
  onCreated,
}: {
  building: BuildingDto;
  onClose: () => void;
  onCreated: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState("Lobby");
  const [profile, setProfile] = useState<ScreenProfile>("lobby");
  const [days, setDays] = useState<Days>(null);
  const [code, setCode] = useState("");
  const [link, setLink] = useState<string | null>(null);
  const [paired, setPaired] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      if (code.trim()) {
        await api(`/buildings/${b.id}/screens/pair`, { method: "POST", body: { name, profile, days, code } });
        setPaired(true);
      } else {
        const res = await api<{ token: string; screen: ScreenDto }>(`/buildings/${b.id}/screens`, {
          method: "POST",
          body: { name, profile, days },
        });
        setLink(`${location.origin}/s/${res.token}`);
      }
      onCreated();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      toast("Link gekopieerd");
    } catch {
      toast("Kopiëren lukt niet; selecteer de link en kopieer hem zelf");
    }
  };

  return (
    <Modal
      title="Nieuw scherm"
      onClose={onClose}
      busy={busy}
      footer={
        link || paired ? (
          <>
            <span className="grow" />
            <button type="button" className="btn primary" onClick={onClose}>
              Klaar
            </button>
          </>
        ) : (
          <>
            <span className="grow" />
            <button type="button" className="btn ghost" onClick={onClose}>
              Annuleren
            </button>
            <button type="button" className="btn primary" onClick={() => void create()} disabled={busy || !name.trim()}>
              {busy ? "Bezig…" : code.trim() ? "Koppelen" : "Link maken"}
            </button>
          </>
        )
      }
    >
      {error && (
        <div className="alert" role="alert">
          {error}
        </div>
      )}
      {paired ? (
        <div className="alert ok" role="status">
          {name.trim()} is gekoppeld. De tv toont het dashboard binnen een paar seconden.
        </div>
      ) : link ? (
        <div className="doc-sharelink panel" role="status">
          <label className="field">
            <span className="label">Link voor {name.trim()}</span>
            <input readOnly value={link} onFocus={(e) => e.target.select()} />
          </label>
          <div className="btnrow">
            <button type="button" className="btn small" onClick={() => void copy()}>
              <IconCopy size={16} aria-hidden="true" /> Kopiëren
            </button>
            <a className="btn small" href={link} target="_blank" rel="noreferrer">
              <IconExternal size={16} aria-hidden="true" /> Openen
            </a>
          </div>
          <span className="hint">
            Kopieer de link nu; hij wordt niet opnieuw getoond. Open hem in de browser van de tv en zet die op volledig
            scherm.
          </span>
        </div>
      ) : (
        <>
          <label className="field">
            <span className="label">Code op de tv</span>
            <input
              value={code}
              maxLength={7}
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              onChange={(e) => setCode(normalizePairCode(e.target.value))}
              placeholder="bijv. K7M 2QX"
            />
            <span className="hint">
              Open op de tv <strong>{location.host}/scherm</strong> en vul de code in die daar staat. Geen code? Laat
              dit leeg, dan krijg je een link om op de tv te openen.
            </span>
          </label>
          <label className="field">
            <span className="label">Naam</span>
            <input
              value={name}
              maxLength={80}
              onChange={(e) => setName(e.target.value)}
              placeholder="bijv. Lobby begane grond"
            />
          </label>
          <fieldset className="q">
            <legend className="ql">Wat mag zichtbaar zijn?</legend>
            {SCREEN_PROFILES.map((p) => (
              <label key={p} className="checkrow">
                <input type="radio" name="screen-profile" checked={profile === p} onChange={() => setProfile(p)} />
                <span>
                  {SCREEN_PROFILE_LABELS[p]}
                  <span className="muted small block">{SCREEN_PROFILE_HINTS[p]}</span>
                </span>
              </label>
            ))}
          </fieldset>
          <label className="field">
            <span className="label">Geldig</span>
            <select
              value={days ?? ""}
              onChange={(e) => setDays(e.target.value ? (Number(e.target.value) as Days) : null)}
            >
              {DAY_LABELS.map((d) => (
                <option key={d.label} value={d.value ?? ""}>
                  {d.label}
                </option>
              ))}
            </select>
          </label>
          <p className="muted small">
            In de lobby staan geen namen van personen of leveranciers, en alleen bevindingen die je zelf op het scherm
            zet (bij de bevinding: Op het scherm in de lobby).
          </p>
        </>
      )}
    </Modal>
  );
}

/** Thema en nachtstand van één scherm; de tv neemt het binnen een minuut over. */
function SettingsDialog({
  screen: s,
  onClose,
  onSaved,
}: {
  screen: ScreenDto;
  onClose: () => void;
  onSaved: (rows: ScreenDto[]) => void;
}) {
  const [theme, setTheme] = useState<ScreenTheme>(s.settings.theme);
  const [nightOn, setNightOn] = useState(Boolean(s.settings.night));
  const [from, setFrom] = useState<number>(s.settings.night?.from ?? SCREEN_NIGHT_DEFAULT.from);
  const [until, setUntil] = useState<number>(s.settings.night?.until ?? SCREEN_NIGHT_DEFAULT.until);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      onSaved(
        await api<ScreenDto[]>(`/screens/${s.id}`, {
          method: "PATCH",
          body: { theme, night: nightOn ? { from, until } : null },
        }),
      );
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const hourSelect = (label: string, value: number, set: (h: number) => void) => (
    <label className="field">
      <span className="label">{label}</span>
      <select value={value} disabled={!nightOn} onChange={(e) => set(Number(e.target.value))}>
        {HOURS.map((h) => (
          <option key={h} value={h}>
            {hh(h)}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <Modal
      title={`${s.name} instellen`}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button
            type="button"
            className="btn primary"
            onClick={() => void save()}
            disabled={busy || (nightOn && from === until)}
          >
            {busy ? "Bezig met opslaan…" : "Opslaan"}
          </button>
        </>
      }
    >
      {error && (
        <div className="alert" role="alert">
          {error}
        </div>
      )}
      <fieldset className="q">
        <legend className="ql">Thema</legend>
        {SCREEN_THEMES.map((t) => (
          <label key={t} className="checkrow">
            <input type="radio" name="screen-theme" checked={theme === t} onChange={() => setTheme(t)} />
            <span>
              {SCREEN_THEME_LABELS[t]}
              <span className="muted small block">
                {t === "dark" ? "Rustig en het mooist op een tv." : "Voor een lichte ruimte of een scherm in de zon."}
              </span>
            </span>
          </label>
        ))}
      </fieldset>
      <fieldset className="q">
        <legend className="ql">Nachtstand</legend>
        <label className="checkrow">
          <input type="checkbox" checked={nightOn} onChange={(e) => setNightOn(e.target.checked)} />
          <span>
            Nachtstand aan
            <span className="muted small block">
              Zwart beeld met alleen de klok, de datum en een eventuele banner. Zet hem uit voor een scherm dat dag en
              nacht bemand is.
            </span>
          </span>
        </label>
        <div className="grid2">
          {hourSelect("Van", from, setFrom)}
          {hourSelect("Tot", until, setUntil)}
        </div>
      </fieldset>
    </Modal>
  );
}
